import { Injectable } from '@angular/core';

import { ApplicationQuery } from '@app/store/application/application.query';
import {
	CUSTOM_DATA_BRAND_ID,
	CUSTOM_DATA_QUOTE_ID,
	CUSTOM_DATA_UTM_CAMPAIGN,
	CUSTOM_DATA_UTM_CONTENT,
	CUSTOM_DATA_UTM_MEDIUM,
	CUSTOM_DATA_UTM_SOURCE,
	CUSTOM_DATA_UTM_TERM,
	CUSTOM_DATA_QUOTE_TYPE,
	SITE_CODE,
	QE_AB_143_ACCIDENT_ONLY_TEST,
	VARIATION_NOT_TARGETED
} from '@constants/kameleoon';

import {
	CustomData,
	KameleoonClient,
	KameleoonError,
	PageView,
	PageViewParametersType,
	SDKConfigurationType,
	TrackConversionParamsType,
} from '@kameleoon/javascript-sdk';
import { UtmParameters } from '@models/UtmParameters';
import { AnalyticsService } from '@services/analytics.service';

import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

import { LoggingService } from './logging.service';
import { OrchestratorService } from './orchestrator/orchestrator.service';
import { SessionService } from './session.service';

interface ActiveTest {
	activeTest: string;
	variation: string;
}

interface ActivePricingFlag {
	activeFlag: string;
	variation: string;
}

@Injectable({
	providedIn: 'root',
})
export class KameleoonService {
	public client: KameleoonClient;
	public visitorCode: string = '';
	private readonly kameleoonEnabled$!: Observable<boolean | undefined>;
	private enabled: boolean | undefined = true;
	private readonly activeTest: string = QE_AB_143_ACCIDENT_ONLY_TEST;
	private readonly activePricingFlag: string = '';
	private visitorVariation: string = VARIATION_NOT_TARGETED;
	private readonly configuration: Partial<SDKConfigurationType> = {
		updateInterval: 1,
		environment: environment.kameleoonEnvironment,
	};

	constructor(
		private readonly appQuery: ApplicationQuery,
		private readonly loggingService: LoggingService,
		private readonly gtmService: AnalyticsService,
		private readonly sessionService: SessionService,
		private readonly orchestratorService: OrchestratorService
	) {
		this.client = new KameleoonClient({
			siteCode: SITE_CODE,
			configuration: this.configuration,
		});

		this.initializeClient();

		this.kameleoonEnabled$ = this.appQuery.getKameleoonEnabled$();
	}

	public trackConversion(goalId: number, revenue?: number): void {
		try {
			const conversionData: TrackConversionParamsType = {
				visitorCode: this.visitorCode,
				goalId,
			};

			if (revenue !== undefined) {
				conversionData.revenue = revenue;
			}

			this.client.trackConversion(conversionData);
		} catch (error) {
			this.loggingService.error(
				'Error tracking conversion: ',
				error as string,
				true
			);
		}
	}

	public reportTestDetails(): void {
		// if Kameleoon is disabled, just return
		if (!this.enabled) return;

		// push to the Test Association endpoint
		this.saveTestToQuoteAnalytics(this.visitorVariation, this.activeTest);

		// log variation to console
		this.loggingService.info(
			['Active Test Result: ', this.activeTest, ` ${this.visitorVariation}`].join('')
		);
	}

	public getActiveTestDetails(saveTestDetails: boolean = false): ActiveTest {
		if (saveTestDetails) {
			this.reportTestDetails();
		}

		return {
			variation: this.visitorVariation,
			activeTest: this.activeTest
		};
	}

	public getActivePricingFlagDetails(): ActivePricingFlag {
		if (!this.enabled) {
			return {
				variation: VARIATION_NOT_TARGETED,
				activeFlag: this.activePricingFlag
			};
		}
		try {
			const variationKey = this.client.getFeatureFlagVariationKey(
				this.visitorCode,
				this.activePricingFlag
			);

			this.loggingService.info(
				['Pricing Test Result: ', this.activePricingFlag, ` ${variationKey}`].join('')
			);

			// push to GTM
			this.gtmService.push({
				event: 'kameleoon_experiment',
				experiment_name: this.activePricingFlag,
				variation_name: variationKey,
			});

			return {
				variation: variationKey,
				activeFlag: this.activePricingFlag
			};
		}
		catch(err) {
			this.loggingService.error(err);

			this.loggingService.info(
				['Pricing Test Result ERROR: ', this.activePricingFlag, ` Error: ${(err as KameleoonError).type}`].join('')
			);

			return {
				variation: VARIATION_NOT_TARGETED,
				activeFlag: this.activePricingFlag
			};
		}
	}

	public getActiveFeatureFlagDetails(featureKey: string, bypass: boolean = false): string {
		// if NOT enabled and NOT bypassed, return not_targeted
		// this bypass is necessary for Stripe to be used regardless of Kam status
		if (!this.enabled && !bypass) {
			return VARIATION_NOT_TARGETED;
		}
		try {
			const variationKey = this.client.getFeatureFlagVariationKey(
				this.visitorCode,
				featureKey
			);

			this.loggingService.info(
				['Feature Flag Result: ', featureKey, ` ${variationKey}`].join('')
			);

			// push to GTM
			this.gtmService.push({
				event: 'kameleoon_experiment',
				experiment_name: featureKey,
				variation_name: variationKey,
			});

			return variationKey;
		} catch(err) {
			this.loggingService.error(err);

			this.loggingService.info(
				['Feature Flag Result ERROR: ', featureKey, ` Error: ${(err as KameleoonError).type}`].join('')
			);

			return VARIATION_NOT_TARGETED;
		}
	}

	public saveTestToQuoteAnalytics(variationKey: string, featureKey: string): void {
		try {
			const quoteID = this.sessionService.getQuoteId();

			const testDetails = {
				experimentVariationName: variationKey,
				experimentDescription: featureKey,
				source: 'kameleoon',
				quoteID,
			};
			// Store test data
			this.orchestratorService
				.saveTestDetails(testDetails)
				.subscribe((data) => {
					if (!data) {
						this.loggingService.error('Error saving test details');
					} else {
						this.loggingService.info(data);
					}
				});
		} catch(err) {
			this.loggingService.error(
				'Error saving test to QuoteAnalytics: ',
				err as string,
				true,
			);
		}
	}

	public setBrand(brand: string): void {
		try {
			this.client.addData(
				this.visitorCode,
				new CustomData(CUSTOM_DATA_BRAND_ID, brand)
			);
		} catch (error) {
			this.loggingService.error(
				'Error setting feature flag: ',
				error as string,
				true
			);
		}
	}

	public setQuoteType(quoteType: string): void {
		try {
			this.client.addData(
				this.visitorCode,
				new CustomData(CUSTOM_DATA_QUOTE_TYPE, quoteType)
			);
		} catch (error) {
			this.loggingService.error(
				'Error setting feature flag: ',
				error as string,
				true
			);
		}
	}

	public setQuoteId(quoteId: string): void {
		try {
			this.client.addData(
				this.visitorCode,
				new CustomData(CUSTOM_DATA_QUOTE_ID, quoteId)
			);
		} catch (error) {
			this.loggingService.error(
				'Error setting feature flag: ',
				error as string,
				true
			);
		}
	}

	public setUtmParams(params: UtmParameters): void {
		const {
			campaign,
			medium,
			source,
			term,
			content
		} = params || {};
		try {
			this.client.addData(
				this.visitorCode,
				new CustomData(CUSTOM_DATA_UTM_TERM, term || ''),
				new CustomData(CUSTOM_DATA_UTM_CAMPAIGN, campaign || ''),
				new CustomData(CUSTOM_DATA_UTM_CONTENT, content || ''),
				new CustomData(CUSTOM_DATA_UTM_MEDIUM, medium || ''),
				new CustomData(CUSTOM_DATA_UTM_SOURCE, source || ''),
			);
		} catch (error) {
			this.loggingService.error(
				'Error setting Kameleoon custom data: ',
				error as string,
				true,
			);
		}
	}

	public addPageView(url: string, title: string): void {
		try {
			const pageViewParameters: PageViewParametersType = {
				urlAddress: url,
				title,
				// TODO we can set referrer here as well
			};
			this.client.addData(this.visitorCode, new PageView(pageViewParameters));
		} catch (error) {
			this.loggingService.error(
				'Error setting feature flag: ',
				error as string,
				true
			);
		}
	}

	public setCustomData(key: number, value: string | number | undefined): void {
		// eslint-disable-next-line eqeqeq
		if (value == null) return;
		try {
			this.client.addData(
				this.visitorCode,
				new CustomData(key, value.toString())
			);
		} catch (error) {
			this.loggingService.error(
				'Error setting custom data: ',
				error as string,
				true
			);
		}
	}

	public async initializeClient(brand: string = ''): Promise<void> {
		try {
			await this.client.initialize();

			this.visitorCode = this.client.getVisitorCode();
			this.loggingService.info(
				`Kameleoon client successfully initialized - (Visitor Code: ${this.visitorCode})`
			);

			// set the brand early so page 1 has the brand ASAP
			if (brand) {
				this.setBrand(brand);
			}

			this.visitorVariation = this.client.getFeatureFlagVariationKey(
				this.visitorCode,
				this.activeTest
			);

			// push to GTM
			this.gtmService.push({
				event: 'kameleoon_experiment',
				experiment_name: this.activeTest,
				variation_name: this.visitorVariation,
			});

			this.kameleoonEnabled$.subscribe(enabled => {
				this.enabled = enabled;
				this.loggingService.info(
					`Kameleoon enabled status: ${enabled ? 'ON' : 'OFF'}`
				);

				if (!this.enabled) {
					this.visitorVariation = VARIATION_NOT_TARGETED;
				} else {
					this.visitorVariation = this.client.getFeatureFlagVariationKey(
						this.visitorCode,
						this.activeTest
					);

					// push to GTM
					this.gtmService.push({
						event: 'kameleoon_experiment',
						experiment_name: this.activeTest,
						variation_name: this.visitorVariation,
					});
				}
			});
		} catch (error) {
			this.loggingService.error(
				'Error initializing Kameleoon client: ',
				error as string,
				true
			);

			this.visitorVariation = VARIATION_NOT_TARGETED;
		}
	}
}
